import "./Navigation.scss";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Nav, Navbar, Image } from "react-bootstrap";
import { TelephoneFill } from "react-bootstrap-icons";
import logoImg from "../assets/logo.svg";

function Navigation() {
  const [activeLink, setactivelink] = useState("home");

  const location = useLocation();
  const navigate = useNavigate();

  const isAutoScrolling = useRef(false);
  const scrollTimer = useRef(null);

  const navLinks = [
    { id: "home", label: "Home" },
    { id: "about-us", label: "About" },
    { id: "services", label: "Services" },
    { id: "clients", label: "Clients" },
    { id: "work", label: "Work" },
    { id: "contact", label: "Contact" },
  ];

  /*
   * Get currently visible section
   */
  const getCurrentSection = () => {
    const offset = 100;
    let current = "home";

    navLinks.forEach((link) => {
      const section = document.getElementById(link.id);

      if (!section) return;

      const top = section.getBoundingClientRect().top;

      if (top <= offset) {
        current = link.id;
      }
    });

    return current;
  };

  /*
   * Handle scroll
   */
  useEffect(() => {
    // Only detect sections on homepage
    if (location.pathname !== "/") {
      return;
    }

    const handleScroll = () => {
      if (isAutoScrolling.current) return;

      const currentSection = getCurrentSection();

      setactivelink(currentSection);
    };

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    // Set initial active section
    setactivelink(getCurrentSection());

    return () => {
      window.removeEventListener("scroll", handleScroll);

      if (scrollTimer.current) {
        clearTimeout(scrollTimer.current);
      }
    };
  }, [location.pathname]);

  /*
   * Handle homepage hash after route navigation
   *
   * Example:
   * /career -> /#about-us
   */
  useEffect(() => {
    if (location.pathname !== "/") {
      return;
    }

    const hash = window.location.hash.substring(1);

    if (!hash) {
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });

      setactivelink("home");
      return;
    }

    const sectionExists = navLinks.some((link) => link.id === hash);

    if (!sectionExists) {
      return;
    }

    // Wait until homepage sections are rendered
    const timer = setTimeout(() => {
      const section = document.getElementById(hash);

      if (!section) return;

      isAutoScrolling.current = true;

      setactivelink(hash);

      const top = section.getBoundingClientRect().top + window.pageYOffset - 60;

      window.scrollTo({
        top: top,
        behavior: "auto",
      });

      setTimeout(() => {
        isAutoScrolling.current = false;
        setactivelink(hash);
      }, 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  /*
   * Navigation click
   */
  const handleNavClick = (event, id) => {
    event.preventDefault();

    /*
     * IMPORTANT:
     * If user is NOT on homepage,
     * navigate to homepage first.
     */
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }

    /*
     * Homepage navigation
     */
    const section = document.getElementById(id);

    if (!section) return;

    // Immediately update active menu
    setactivelink(id);

    // Lock active menu during smooth scrolling
    isAutoScrolling.current = true;

    const top = section.getBoundingClientRect().top + window.pageYOffset - 60;

    window.scrollTo({
      top: top,
      behavior: "smooth",
    });

    /*
     * Update URL
     */
    window.history.pushState(null, "", `/#${id}`);

    /*
     * Wait for smooth scroll
     */
    if (scrollTimer.current) {
      clearTimeout(scrollTimer.current);
    }

    scrollTimer.current = setTimeout(() => {
      isAutoScrolling.current = false;
      setactivelink(id);
    }, 800);
  };

  /*
   * Career click
   */
  const handleCareerClick = () => {
    setactivelink("career");
  };

  /*
   * Logo click
   */
  const handleLogoClick = (event) => {
    event.preventDefault();

    if (location.pathname !== "/") {
      navigate("/");
      return;
    }

    const homeSection = document.getElementById("home");

    if (!homeSection) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      window.history.pushState(null, "", "/");

      setactivelink("home");

      return;
    }

    isAutoScrolling.current = true;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    window.history.pushState(null, "", "/");

    setactivelink("home");

    if (scrollTimer.current) {
      clearTimeout(scrollTimer.current);
    }

    scrollTimer.current = setTimeout(() => {
      isAutoScrolling.current = false;
      setactivelink("home");
    }, 800);
  };

  useEffect(() => {
    if (location.pathname.startsWith("/work")) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return (
    <Navbar fixed="top" collapseOnSelect expand="lg" className="navbar-primary">
      {/* LOGO */}
      <Navbar.Brand href="/" onClick={handleLogoClick}>
        <Image src={logoImg} alt="Cubetech Studio" width="120" />
      </Navbar.Brand>

      {/* MOBILE PHONE */}
      <Nav.Link href="tel:9270323478" className="btn-danger">
        <TelephoneFill className="me-2" />
        92703 23478
      </Nav.Link>

      <Navbar.Toggle aria-controls="responsive-navbar-nav" />

      <Navbar.Collapse id="responsive-navbar-nav">
        <Nav>
          {navLinks.map((link) => {
            // 1. Check if it's the home page and the scroll section matches
            const isHomeActive =
              location.pathname === "/" && activeLink === link.id;

            // 2. Check if the link is 'work' and the URL path starts with /work
            const isWorkPathActive =
              link.id === "work" && location.pathname.startsWith("/work");

            return (
              <Link
                key={link.id}
                to={`/#${link.id}`}
                className={
                  isHomeActive || isWorkPathActive
                    ? "nav-link active"
                    : "nav-link"
                }
                onClick={(e) => handleNavClick(e, link.id)}
              >
                {link.label}
              </Link>
            );
          })}

          {/* CAREER */}
          <Link
            to="/career"
            className={
              location.pathname === "/career" ||
              location.pathname === "/career/pre-interview-form" ||
              location.pathname === "/career/pre-interview-desinger"
                ? "nav-link active"
                : "nav-link"
            }
            onClick={handleCareerClick}
          >
            Career
          </Link>
        </Nav>

        {/* DESKTOP PHONE */}
        <Nav className="call-btn">
          <Nav.Link href="tel:9270323478" className="btn-danger">
            <TelephoneFill className="me-2" />
            92703 23478
          </Nav.Link>
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  );
}

export default Navigation;
